import { APIService, type ErrorResponse, type RequestBody } from '../../api/apiService';
import { mergeRemotegrantAgentApiPayload } from '../../api/httpClient/remotegrantAgentApiPayload';
import { getTransferBaseUrl } from '../../lib/transferBaseUrl';
import { useAppStore } from '../../store/store';
import { solvePow, type PowChallenge } from '../../utils/pow';
import { authService } from '../authService';

/**
 * Request header for the last upload chunk: SHA-256 digest of the full file (base64).
 * Matches the `X-Content-SHA256` response header used on download integrity checks.
 */
export const UPLOAD_CONTENT_HASH_HEADER = 'X-Content-SHA256';

export interface TransferUploadOptions {
  /** Base64 SHA-256 of the complete file; sent only on the last chunk. */
  contentHash?: string;
}

/**
 * Session-scoped memo of whether the API currently enforces proof-of-work, mirroring the backend
 * POW_ENABLED gate. It lets uploads pick a path without per-request overhead when PoW is off:
 *  - undefined (unknown) / false → assume off: send the first chunk immediately (zero overhead). If
 *    the API actually requires PoW it answers 428 once; we recover reactively and flip this to true.
 *  - true → pre-fetch and solve a challenge BEFORE the first chunk, so its (possibly large) body is
 *    never uploaded twice.
 * Self-heals back to false if a later challenge probe reports PoW disabled.
 */
let powRequired: boolean | undefined;

/**
 * TransferService provides methods for handling file transfer operations,
 * including process status retrieval, file download, file upload (with progress),
 * and branding image upload. Extends the base APIService for HTTP requests.
 */
export class TransferService extends APIService {
  /**
   * Retrieves the status of a transfer process by its process ID.
   * @param processId - The unique identifier of the transfer process.
   * @returns A promise resolving to the process status or undefined.
   */
  getProcessStatus(processId: string): Promise<any | undefined> {
    return this.request(
      `/transfer/status/${processId}`,
      'GET',
      undefined,
      undefined,
      getTransferBaseUrl(),
    );
  }

  /**
   * Initiates a file download operation for the specified payload.
   * Handles both binary and JSON error responses.
   * @param payload - The request body containing file IDs to download.
   * @returns A promise resolving to an object containing either a Blob (file) or JSON (error/info).
   *
   * Example payload:
   * {
   *   "filesId": [0]
   * }
   */
  async download(
    payload: RequestBody,
    options?: {
      signal?: AbortSignal;
      onDownloadProgress?: (progressPercent: number) => void;
    },
  ): Promise<
    | { kind: 'blob'; data: Blob; headers: any; status: number }
    | { kind: 'json'; data: any; status: number }
  > {
    // Perform the download request and expect a Blob response.
    const res = await this.requestRaw<Blob>(
      '/transfer/download',
      'POST',
      payload,
      undefined,
      getTransferBaseUrl(),
      {
        responseType: 'blob',
        rawResponse: true,
        validateStatus: () => true,
        signal: options?.signal,
        onDownloadProgress: (progressEvent) => {
          if (!options?.onDownloadProgress || !progressEvent.total) {
            return;
          }
          const progressPercent = Math.min(
            100,
            Math.round((progressEvent.loaded / progressEvent.total) * 100),
          );
          options.onDownloadProgress(progressPercent);
        },
      },
    );

    const contentType = String(res.headers?.['content-type'] || '').toLowerCase();

    // If the response is JSON (e.g., error or info), parse and return as JSON.
    if (contentType.includes('application/json')) {
      const text = await (res.data as Blob).text();
      const json = JSON.parse(text);
      return { kind: 'json', data: json, status: res.status };
    }

    // Otherwise, treat the response as a binary file (Blob).
    return { kind: 'blob', data: res.data as Blob, headers: res.headers, status: res.status };
  }

  /**
   * Uploads a file or file chunk to the server using XMLHttpRequest to support progress events.
   * Handles authentication and error normalization.
   *
   * @param payload - The FormData containing file data and metadata.
   * @param onUploadProgress - Optional callback for upload progress events.
   * @param signal - Optional AbortSignal to cancel the upload; when aborted, rejects with error.aborted === true.
   * @param options - Optional upload metadata (e.g. `contentHash` on the last chunk).
   * @returns A promise resolving with the server response or rejecting with a normalized error.
   *
   * Example payload fields:
   *   file: File;           // The file to be uploaded.
   *   blockId: string;      // The progressive number of the current chunk (timestamp_progressiveNumber).
   *   isLastChunk: boolean; // Whether this is the last chunk of the file.
   *   filename: string;     // The name of the file.
   *   cufi?: string;        // Unique file identifier for subsequent chunks.
   *   parentId?: number;    // ID of the parent folder, if applicable.
   *   onBehalfOf?: number;  // ID of the user on whose behalf the file is uploaded, if applicable.
   */
  async upload(
    payload: FormData,
    onUploadProgress?: (progressEvent: ProgressEvent) => void,
    signal?: AbortSignal,
    options?: TransferUploadOptions,
  ): Promise<any | undefined> {
    // Proof-of-work applies only to the FIRST chunk of an upload (the one without a server-assigned
    // cufi); continuation chunks are exempt, so a file costs at most one solve. When we already know
    // PoW is enforced, pre-solve a challenge and attach X-PoW from the start, so the first chunk's
    // (possibly large) body is uploaded only once. When PoW is off — the default — send straight away
    // with zero overhead; if the API unexpectedly demands PoW, sendUploadChunk recovers from the 428
    // and remembers it for next time. See utils/pow.ts and README/RATE_LIMITING.md.
    const isFirstChunk = !payload.get('cufi');
    const powToken =
      isFirstChunk && powRequired === true ? await this.acquireUploadPowToken() : undefined;
    return this.sendUploadChunk(payload, onUploadProgress, signal, options, powToken);
  }

  /**
   * Fetches a fresh upload challenge and solves it, returning the X-PoW token for the first chunk.
   * Returns undefined (and records that PoW is off) when the API reports it disabled, or on any
   * failure — it never blocks the upload, since the backend filter still answers 428 if a token is
   * genuinely required.
   */
  private async acquireUploadPowToken(): Promise<string | undefined> {
    try {
      const res = await this.request<{ challenge?: PowChallenge; enabled: boolean }>(
        '/pow/challenge?scope=upload',
        'GET',
        undefined,
        undefined,
        getTransferBaseUrl(),
      );
      powRequired = res.enabled;
      if (!res.enabled || !res.challenge) return undefined;
      return await solvePow(res.challenge);
    } catch (error) {
      console.error('Upload proof-of-work challenge could not be acquired:', error);
      return undefined;
    }
  }

  /**
   * Sends a single upload chunk via XMLHttpRequest. On a 428 (proof-of-work required, only ever the
   * first chunk) it solves the challenge and retries this chunk once with the X-PoW header.
   * @param powToken - Solved X-PoW token to attach; set only on the post-428 retry to avoid loops.
   */
  private sendUploadChunk(
    payload: FormData,
    onUploadProgress?: (progressEvent: ProgressEvent) => void,
    signal?: AbortSignal,
    options?: TransferUploadOptions,
    powToken?: string,
  ): Promise<any | undefined> {
    const endpoint = '/transfer/upload';
    const transferBaseUrl = getTransferBaseUrl();
    const srToken = useAppStore.getState().authenticatedUser?.srToken;
    mergeRemotegrantAgentApiPayload(payload, transferBaseUrl, srToken ? { srToken } : {});
    const url = `${transferBaseUrl}${endpoint}`;

    // Retrieve authentication token using centralized authService
    const token = authService.getToken();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      const cleanup = () => {
        if (signal && onAbort) {
          signal.removeEventListener('abort', onAbort);
        }
      };

      const onAbort = () => {
        xhr.abort();
      };

      if (signal) {
        if (signal.aborted) {
          const err = Object.assign(new Error('ABORTED'), { aborted: true });
          reject(err);
          return;
        }
        signal.addEventListener('abort', onAbort);
      }

      xhr.open('POST', url, true);

      // Set Authorization header if a token is available.
      if (token) {
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);
      }

      // Add folder password sessions header (same as httpClient) for uploads into password-protected folders.
      const headerValue = useAppStore.getState().getFilePasswordSessionHeaderValue?.();
      if (headerValue) {
        xhr.setRequestHeader('X-Folder-Password-Sessions', headerValue);
      }

      const contentHash = options?.contentHash?.trim();
      if (contentHash) {
        xhr.setRequestHeader(UPLOAD_CONTENT_HASH_HEADER, contentHash);
      }

      // Attach the solved proof-of-work token on the retry that follows a 428.
      if (powToken) {
        xhr.setRequestHeader('X-PoW', powToken);
      }

      // Attach upload progress event handler if provided.
      if (onUploadProgress && xhr.upload) {
        xhr.upload.onprogress = onUploadProgress;
      }

      // Handle abort (user cancelled upload).
      xhr.onabort = () => {
        cleanup();
        const err = Object.assign(new Error('ABORTED'), { aborted: true });
        reject(err);
      };

      // Handle successful upload completion.
      xhr.onload = () => {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) {
          const contentType = xhr.getResponseHeader('Content-Type');
          // If the response is JSON, parse and resolve with the parsed object.
          if (contentType && contentType.indexOf('application/json') !== -1) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              // If JSON parsing fails, resolve with the raw response text.
              console.error('Error parsing JSON response', e);
              resolve(xhr.responseText);
            }
          } else {
            // For non-JSON responses, resolve with the raw response.
            resolve(xhr.response);
          }
        } else if (xhr.status === 428 && !powToken) {
          // First-chunk proof-of-work required but not pre-solved (PoW was just enabled, or our
          // proactive probe failed). Remember it so subsequent uploads pre-solve, then solve from
          // the 428 body and retry just this chunk once.
          powRequired = true;
          this.solveUploadPow(xhr.responseText)
            .then((solved) => {
              if (solved) {
                resolve(this.sendUploadChunk(payload, onUploadProgress, signal, options, solved));
              } else {
                reject(this.buildUploadError(xhr));
              }
            })
            .catch(() => reject(this.buildUploadError(xhr)));
        } else {
          // For HTTP errors, construct and reject with a normalized ErrorResponse.
          reject(this.buildUploadError(xhr));
        }
      };

      // Handle network or request errors.
      xhr.onerror = () => {
        cleanup();
        const error: ErrorResponse = new Error(xhr.statusText) as ErrorResponse;
        error.statusText = xhr.statusText;
        error.status = xhr.status;
        reject(error);
      };

      // Send the FormData payload to the server.
      xhr.send(payload);
    });
  }

  /**
   * Parses the 428 response body and solves its proof-of-work challenge.
   * @returns The X-PoW token, or undefined if no challenge was present or solving failed.
   */
  private async solveUploadPow(responseText: string): Promise<string | undefined> {
    try {
      const body = JSON.parse(responseText) as { challenge?: PowChallenge };
      if (!body.challenge) return undefined;
      return await solvePow(body.challenge);
    } catch (error) {
      console.error('Upload proof-of-work challenge could not be solved:', error);
      return undefined;
    }
  }

  /** Builds a normalized ErrorResponse from a failed upload XHR, parsing a JSON body when present. */
  private buildUploadError(xhr: XMLHttpRequest): ErrorResponse {
    const error: ErrorResponse = new Error(xhr.statusText) as ErrorResponse;
    error.statusText = xhr.statusText;
    error.status = xhr.status;

    try {
      const contentType = xhr.getResponseHeader('Content-Type');
      if (contentType && contentType.indexOf('application/json') !== -1 && xhr.responseText) {
        error.response = JSON.parse(xhr.responseText);
      } else if (xhr.responseText) {
        error.response = xhr.responseText;
      }
    } catch {
      if (xhr.responseText) {
        error.response = xhr.responseText;
      }
    }

    return error;
  }

  /**
   * Uploads a branding image to the server.
   * @param payload - The FormData containing the branding image file.
   * @returns A promise resolving with the server response or undefined.
   */
  uploadBrandingImage(payload: FormData): Promise<any | undefined> {
    return this.request(
      `/transfer/upload/images/branding`,
      'POST',
      payload,
      undefined,
      getTransferBaseUrl(),
    );
  }
}
